import React from "react";
import { useTranslation } from "react-i18next";
import { AssetRecommendationRowView, createAssetRequestsForEmployees } from "../../../../api/General/assets";
import { Dialog, DialogContent, DialogBody, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/Dialog";
import Button from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";

interface Props {
  row: AssetRecommendationRowView | null;
  onClose: () => void;
  onTransferred: (count: number) => void;
  /** The asset manager's pick from the drawer, held by AssetOverviewTab. It outranks the AI's. */
  selectedSkuFor: (categoryId: number, employeeId: string) => number | undefined;
}

export default function TransferRecommendationModal({ row, onClose, onTransferred, selectedSkuFor }: Props) {
  const { t } = useTranslation();
  const [selected, setSelected] = React.useState<string[]>([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setSelected(row ? row.employees.map((e) => e.employeeId) : []);
    setError(null);
  }, [row]);

  const cap = row?.available ?? 0;
  const atCap = selected.length >= cap;

  // The row is an L2 category, so each request needs the L3 SKU that person actually gets.
  const skuFor = React.useCallback(
    (employeeId: string) => {
      if (!row) return undefined;
      const employee = row.employees.find((e) => e.employeeId === employeeId);
      // Manual pick wins, the AI pick is the fallback, stock order is the last resort.
      const picked = selectedSkuFor(row.categoryId, employeeId) ?? employee?.suggestedCategoryId ?? row.skus[0]?.categoryId;
      if (picked == null) return undefined;
      const sku = row.skus.find((s) => s.categoryId === picked);
      return sku ?? (employee?.suggestedSkuName ? { categoryId: picked, skuName: employee.suggestedSkuName, available: 0 } : undefined);
    },
    [row, selectedSkuFor]
  );

  const transferable = selected.filter((id) => skuFor(id) !== undefined);

  const toggle = (employeeId: string) => {
    setSelected((prev) => (prev.includes(employeeId) ? prev.filter((id) => id !== employeeId) : [...prev, employeeId]));
  };

  const handleSubmit = async () => {
    if (!row) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await createAssetRequestsForEmployees({
        categoryId: row.categoryId,
        employees: transferable.map((employeeId) => ({ employeeId, skuCategoryId: skuFor(employeeId)!.categoryId })),
      });
      if (result.errors.length === 0) {
        onTransferred(result.payload);
      } else {
        setError(result.errors[0]?.description || t("assetOverview.transferError"));
      }
    } catch {
      setError(t("assetOverview.transferError"));
    } finally {
      setSubmitting(false);
    }
  };

  // Every dismissal path locks together while submitting — never some-but-not-others.
  const guard = (e: Event) => { if (submitting) e.preventDefault(); };

  return (
    <Dialog open={row !== null} onOpenChange={(open) => { if (!open && !submitting) onClose(); }}>
      <DialogContent onEscapeKeyDown={guard} onPointerDownOutside={guard} data-testid="overview-transfer-modal">
        {row && (
          <>
            <DialogHeader>
              <DialogTitle>{t("assetOverview.transferTitle", { sku: row.categoryName })}</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <p className="text-sm text-muted-foreground">{t("assetOverview.transferHint")}</p>
              <p className="mt-4 text-sm font-medium text-foreground">{t("assetOverview.selectEmployees")}</p>
              <ul className="mt-2 space-y-2 max-h-64 overflow-y-auto">
                {row.employees.map((e) => {
                  const isSelected = selected.includes(e.employeeId);
                  const sku = skuFor(e.employeeId);
                  const disabled = submitting || sku === undefined || (!isSelected && atCap);
                  return (
                    <li key={e.employeeId}>
                      <button
                        type="button"
                        onClick={() => toggle(e.employeeId)}
                        disabled={disabled}
                        aria-pressed={isSelected}
                        className={`w-full rounded-lg border p-3 text-start transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:opacity-50 disabled:cursor-not-allowed ${
                          isSelected
                            ? "border-primary bg-primary/5 text-foreground"
                            : "border-border bg-card text-muted-foreground hover:border-border/80"
                        }`}
                        data-testid={`overview-transfer-employee-${e.employeeId}`}
                      >
                        <span className="block text-sm font-medium">{e.employeeName}</span>
                        {sku && (
                          <span
                            className="mt-0.5 block text-xs font-medium text-primary"
                            data-testid={`overview-transfer-sku-${e.employeeId}`}
                          >
                            {t("assetOverview.transferGets", { name: sku.skuName })}
                          </span>
                        )}
                        <span className="mt-0.5 block text-xs text-muted-foreground">{e.reason}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              {atCap && (
                <p className="mt-2 text-xs text-muted-foreground" data-testid="overview-transfer-cap-note">
                  {t("assetOverview.transferCap", { count: cap })}
                </p>
              )}
              {error && (
                <p className="mt-3 text-sm text-destructive" role="alert" data-testid="overview-transfer-error">
                  {error}
                </p>
              )}
            </DialogBody>
            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={submitting}>
                {t("Cancel")}
              </Button>
              <Button onClick={handleSubmit} disabled={submitting || transferable.length === 0} data-testid="overview-transfer-submit">
                {submitting && <Spinner size="sm" className="me-2" />}
                {submitting ? t("assetOverview.transferSubmitting") : t("assetOverview.transferSubmit", { count: transferable.length })}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
